import { Action, Store } from 'redux';
import * as api from '../api/api';
import HttpMethods from '../../enums/http-methods';
import * as sessionActions from '../../actions/session.actions';
import { AuthHandler } from '../api/auth';
import { ActivityMonitor } from '../api/activity-monitor';
const {
  sessionTimeout,
  sessionActivityPrompt,
  sessionReset,
  sessionRefresh
} = sessionActions;

/**
 * This function observes the events (TIMEOUT, INACTIVITY WARNINGS, REFRESH) on a session.
 * @param activityMonitor - the session to be observed
 * @param store - the store on which to dispatch activities
 * @param authHandler - for token updates
 */
const observeActivity = (
  activityMonitor: Partial<typeof ActivityMonitor>,
  store: Store,
  authHandler: Partial<typeof AuthHandler>,
  onTimeout?: () => void
) => {
  activityMonitor.on(ActivityMonitor.events.BEGIN_REFRESH, async () => {
    // On a refresh, we get a new token by choosing the same context that we
    // have in the current context.  The net effect is that the app context
    // does not change on the user, but a new token is retrieved.
    store.dispatch(sessionRefresh() as Action);
    const freshContext = await api.requestJSON<CMxAPI.ContextAPI>(
      `/contexts/v1`,
      {
        method: HttpMethods.PUT,
        body: JSON.stringify({ organizationId: activityMonitor.contextId })
      }
    );
    // expiryTimeToLive is a configurable property per environment and
    // perhaps in the future per tenant.
    activityMonitor.expiryTimeout = freshContext.expiryTimeToLive;
    authHandler.token = freshContext.jwtToken;

    store.dispatch(sessionReset() as Action);
  });

  activityMonitor.on(ActivityMonitor.events.ACTIVITY_PROMPT, () => {
    // dispatch prompt for user to refresh session.
    store.dispatch(sessionActivityPrompt() as Action);
  });

  activityMonitor.on(ActivityMonitor.events.TIMEOUT, () => {
    // also clear up any data that may have been stored
    sessionStorage.clear();
    authHandler.token = '';
    store.dispatch(sessionTimeout() as Action);
    onTimeout?.();
  });
};

// creating the session once so that it is importable
// into other places (ie. actions);
const activityMonitor = new ActivityMonitor();

export { observeActivity, activityMonitor };
